/**
 * t611u: a hint line an Owner can reword for the whole workspace. Shows the workspace's text if it has one, else
 * `default`. With "Edit hints" on (owners only), the line gets a dotted outline; clicking it edits it in place.
 * Plain text only: rendered as text (React escapes it), line breaks kept, at most TEXT_OVERRIDE_MAX characters.
 * `id` must be a stable key, `<group>.<item>.<slot>`, never derived from the text.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Pencil } from 'lucide-react';
import clsx from 'clsx';
import { normaliseOverrideText, TEXT_OVERRIDE_MAX } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { textOverridesKey, useHintEditing, useTextOverrides } from '@/lib/textOverrides';
import { Button, Textarea } from './index';

type Tag = 'p' | 'span' | 'div';

export function EditableHint({ id, default: fallback, as: Tag = 'span', className, testId }: { id: string; default: string; as?: Tag; className?: string; testId?: string }) {
  const { ws, map, canEdit } = useTextOverrides();
  const editing = useHintEditing(s => s.on);
  const toast = useUi(s => s.toast);
  const custom = map.get(id);
  const text = custom ?? fallback;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(text);
  const [busy, setBusy] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { if (!editing) setOpen(false); }, [editing]);
  useEffect(() => { if (open) area.current?.focus(); }, [open]);

  const common = { 'data-hint': id, 'data-testid': testId };
  if (!canEdit || !editing) return <Tag className={clsx('whitespace-pre-line', className)} {...common}>{text}</Tag>;

  const start = () => { setDraft(text); setOpen(true); };
  const done = (view: Awaited<ReturnType<typeof api.textOverrides>>) => { queryClient.setQueryData(textOverridesKey(ws!), view); setOpen(false); };
  const run = async (fn: () => ReturnType<typeof api.textOverrides>, msg: string) => {
    setBusy(true);
    try { done(await fn()); toast(msg, 'success'); } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  const n = normaliseOverrideText(draft);
  const save = () => {
    if ('error' in n) return;
    if (n.text === fallback.trim()) return custom === undefined ? setOpen(false) : void run(() => api.resetTextOverride(ws!, id), 'Hint reset to the default');
    if (n.text === custom) return setOpen(false);
    void run(() => api.setTextOverride(ws!, id, n.text), 'Hint saved');
  };
  const reset = () => void run(() => api.resetTextOverride(ws!, id), 'Hint reset to the default');
  const keys = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); save(); }
  };

  if (!open) {
    return (
      <Tag role="button" tabIndex={0} title="Edit this hint" aria-label={`Edit hint: ${text}`} {...common}
        onClick={start} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); start(); } }}
        className={clsx('whitespace-pre-line cursor-text rounded-sm outline-1 outline-offset-2 outline-dotted outline-slate-400 hover:outline-slate-600 focus-visible:outline-2 dark:outline-slate-500 dark:hover:outline-slate-300', className)}>
        {text}
      </Tag>
    );
  }
  const len = [...draft.trim()].length;
  return (
    <Tag className={clsx('block', className)} {...common} data-editing="">
      <Textarea ref={area} width="full" rows={Math.min(6, Math.max(2, Math.ceil(draft.length / 80)))} value={draft} maxLength={TEXT_OVERRIDE_MAX + 50}
        onChange={e => setDraft(e.target.value)} onKeyDown={keys} aria-label="Hint text" data-testid="hint-editor" className="text-sm text-slate-900" />
      <span className="mt-1.5 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={save} disabled={busy || 'error' in n} loading={busy}>Save</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
        {custom !== undefined && <Button size="sm" variant="ghost" onClick={reset} disabled={busy} title={`Default: ${fallback}`}>Reset to default</Button>}
        <span className={clsx('ml-auto text-xs tabular-nums', len > TEXT_OVERRIDE_MAX ? 'text-rose-700 dark:text-rose-400' : 'text-slate-500')}>{len} / {TEXT_OVERRIDE_MAX}</span>
      </span>
      {'error' in n && draft.trim() !== '' && <span className="mt-1 block text-xs text-rose-700 dark:text-rose-400">{n.error}</span>}
    </Tag>
  );
}

/** "Edit hints" switch for a page header. Owners only; everyone else never sees it. Turns itself off when the page closes. */
export function EditHintsToggle({ className }: { className?: string }) {
  const { canEdit } = useTextOverrides();
  const { on, set } = useHintEditing();
  useEffect(() => () => set(false), [set]);
  if (!canEdit) return null;
  return (
    <Button size="sm" variant="ghost" aria-pressed={on} onClick={() => set(!on)} className={clsx('text-slate-500', className)} data-testid="edit-hints"
      title={on ? 'Stop editing hints' : 'Change the hint text on this page for everyone in your workspace'}>
      <Pencil className="h-3.5 w-3.5" aria-hidden />{on ? 'Done' : 'Edit hints'}
    </Button>
  );
}
